/* eslint-disable no-underscore-dangle */
import { useQuery } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'

import { relayConnectionToArray } from 'utils/transformations/graphql'
import {
  filterByNonEmptyLineItems,
  getLineItems,
  getLineItemsByUniqueStyleColors,
  groupLineItemsByStyle,
} from 'utils/transformations/orders'

import { graphene } from 'graphql/tags'

const orderFragment = graphene`#graphql
  fragment OrderFragment on Order {
    id
    referenceNumber
    lineItems(filterBy: { onlyEmptyStyleColors: true }) {
      edges {
        node {
          id
          styleColor {
            id
            swatch {
              id
              url
              colorNumber
            }
            name
            code
            style {
              id
              name
              code
              images {
                edges {
                  node {
                    id
                    image {
                      id
                      url
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export const BrandOrdersQuery = graphene`#graphql
  query orders($selectedOrdersIds: [ID]) {
    viewerBrand {
      id
      orders(filterBy: { ids: $selectedOrdersIds }) {
        totalCount
        edges {
          node {
            ...OrderFragment
          }
        }
      }
    }
  }
  ${orderFragment}
`

export const RetailerOrdersQuery = graphene`#graphql
  query orders($selectedOrdersIds: [ID]) {
    viewer {
      id
      viewerAccount {
        ... on RetailerAccount {
          id
          orders(filterBy: { ids: $selectedOrdersIds }) {
            totalCount
            edges {
              node {
                ...OrderFragment
              }
            }
          }
        }
      }
    }
  }
  ${orderFragment}
`

const getFilteredOrders = (orders) => {
  const formattedOrders = relayConnectionToArray(orders) || []
  const transformedOrders = formattedOrders.map((formattedOrder) => ({
    ...formattedOrder,
    lineItems: getLineItems(formattedOrder.lineItems),
  }))

  const ordersFilteredByNonEmptyLineItems =
    filterByNonEmptyLineItems(transformedOrders)
  const ordersFilteredLineItemsByUniqueStyleColors =
    getLineItemsByUniqueStyleColors(ordersFilteredByNonEmptyLineItems)

  const ordersGroupedByStyle = groupLineItemsByStyle(
    ordersFilteredLineItemsByUniqueStyleColors,
  )

  return ordersGroupedByStyle.filter((order) => order.uniqueStyles.length > 0)
}

export const withOrders = (WrappedComponent) => (props) => {
  const { selectedOrdersIds, isRetailer } = props

  const query = isRetailer ? RetailerOrdersQuery : BrandOrdersQuery

  const { loading, data, error } = useQuery(query, {
    variables: {
      selectedOrdersIds,
    },
    skip: isEmpty(selectedOrdersIds),
  })

  const orders = isRetailer
    ? data?.viewer?.viewerAccount?.orders
    : data?.viewerBrand?.orders

  const customProps = {
    ...props,
    ordersQueryLoading: loading,
    orders: !loading && !error && orders ? getFilteredOrders(orders) : [],
  }

  return <WrappedComponent {...customProps} />
}
